import { InsightsDimension } from '../hooks/use-insights-filters';
import { EmissionsCategoryEnum, InsightsGroupByItem } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import Text from '@shared/components/content/text';
import Unit, { renderUnit } from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import {
  formatCompact,
  formatCost,
  getCurrencySymbol,
} from '@shared/lib/format';
import { ColumnDef } from '@tanstack/react-table';

const EMISSIONS_COLOR = 'var(--chart-food-and-drink)';
const SPEND_COLOR = 'var(--spend-400)';

type ColumnContext = {
  dimensionLabel: string;
  dimension: InsightsDimension;
  /** The view sits under the Packaging category, where food ratings mean nothing. */
  packagingScope: boolean;
  maxEmissions: number;
  maxSpend: number;
  /** Without the cost feature the spend columns stay visible but locked:
   *  padlocked header, no sorting, values withheld. */
  hasCostFeature: boolean;
  currency?: string | null;
};

/** Groupings whose rows carry a banded A–E rating worth showing. */
const RATED_DIMENSIONS: InsightsDimension[] = [
  'productCategory',
  'productSubCategory',
  'productBase',
  'item',
];

/** The Packaging category's slug, which no food rating applies beneath. */
export const PACKAGING_CATEGORY_SLUG = 'packaging';

/**
 * The row's rating as a cloud, or null. Packaging has no meaningful food
 * rating, so packaging rows show nothing.
 */
function rowRating(
  row: InsightsGroupByItem,
  dimension: InsightsDimension,
  packagingScope: boolean
): EmissionsCategoryEnum | null {
  if (!RATED_DIMENSIONS.includes(dimension)) return null;
  if (
    packagingScope ||
    row.itemType === 'packaging' ||
    row.slug === PACKAGING_CATEGORY_SLUG
  ) {
    return null;
  }
  const rating = row.rating?.toUpperCase();
  return rating && 'ABCDE'.includes(rating)
    ? (rating as EmissionsCategoryEnum)
    : null;
}

/** Header key for the mini-bars: a swatch and label per shown measure. */
function MiniBarLegend({ showSpend }: { showSpend: boolean }) {
  return (
    <div className="flex items-center gap-3">
      {[
        { label: 'Emissions', color: EMISSIONS_COLOR },
        ...(showSpend ? [{ label: 'Spend', color: SPEND_COLOR }] : []),
      ].map(({ label, color }) => (
        <span key={label} className="flex items-center gap-1">
          <span
            className="size-2 rounded-sm"
            style={{ backgroundColor: color }}
          />
          <Text variant="unit" className="text-xs leading-[12px]">
            {label}
          </Text>
        </span>
      ))}
    </div>
  );
}

/** One of the two stacked proportion bars beneath a breakdown row's name. */
function MiniBar({
  value,
  max,
  color,
}: {
  value: number | null;
  max: number;
  color: string;
}) {
  const pct = value != null && max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="h-[5px] w-full max-w-60 overflow-hidden rounded-[3px] bg-gray-50">
      <div
        className="h-full rounded-[3px]"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

const spendValue = (row: InsightsGroupByItem) =>
  row.spend != null ? Number(row.spend) : null;
const spendPerKgValue = (row: InsightsGroupByItem) =>
  row.spendPerKg != null ? Number(row.spendPerKg) : null;

export function getInsightsBreakdownColumns({
  dimensionLabel,
  dimension,
  packagingScope,
  maxEmissions,
  maxSpend,
  hasCostFeature,
  currency,
}: ColumnContext): ColumnDef<InsightsGroupByItem>[] {
  const showRatings = RATED_DIMENSIONS.includes(dimension) && !packagingScope;
  const symbol = getCurrencySymbol(currency) || '£';
  const spendLocked = !hasCostFeature;
  // Locked columns keep their header for visibility, padlocked and muted.
  const lockableName = (name: string) =>
    spendLocked ? (
      <span className="flex items-center gap-1.5 text-gray-400">
        <Icons.lock className="size-3.5 shrink-0" />
        {name}
      </span>
    ) : (
      name
    );
  // Values are withheld on locked columns even when the response carries them.
  const lockedCell = (
    <TextCell text="-" type="number" className="text-gray-300" />
  );

  return [
    {
      id: 'name',
      accessorKey: 'name',
      enableSorting: false,
      // Not HeaderCell: it centres headers that carry a unit row, and this
      // left-aligned column wants the legend under the name. Same geometry.
      header: () => (
        <div className="flex flex-col pb-1 pt-5">
          <div className="flex h-5 items-center">
            <p>{dimensionLabel}</p>
          </div>
          <div className="h-3">
            <MiniBarLegend showSpend={!spendLocked} />
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col gap-1.5 py-0.5">
          <Text className="truncate text-sm font-medium text-primary-950">
            {row.original.name}
          </Text>
          <div className="flex max-w-60 flex-col gap-1">
            <MiniBar
              value={row.original.emissionsTonnes}
              max={maxEmissions}
              color={EMISSIONS_COLOR}
            />
            {!spendLocked && (
              <MiniBar
                value={spendValue(row.original)}
                max={maxSpend}
                color={SPEND_COLOR}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      // Column ids double as the API sort keys.
      id: 'emissions',
      accessorKey: 'emissionsTonnes',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          unit={<Unit variant="tCO2e" />}
          numberColumn
        />
      ),
      cell: ({ row }) => (
        <NumberCell number={row.original.emissionsTonnes as number} />
      ),
    },
    {
      id: 'emissionsPerKg',
      accessorKey: 'emissionsPerKg',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Carbon intensity"
          unit={<Unit variant="kgCO2e/kg" />}
          numberColumn
        />
      ),
      cell: ({ row }) => {
        const rating = rowRating(row.original, dimension, packagingScope);
        if (!showRatings) {
          return <NumberCell number={row.original.emissionsPerKg as number} />;
        }
        // A compact right-packed cluster: fixed cloud slot, then a fixed-
        // width number, so clouds and figures each align down the column
        // without stretching it beyond its header.
        return (
          <div className="flex items-center justify-end gap-2">
            <span className="flex w-9 shrink-0 justify-start">
              {rating && <RatingCloud rating={rating} className="w-8" />}
            </span>
            <NumberCell
              number={row.original.emissionsPerKg as number}
              className="min-w-12"
            />
          </div>
        );
      },
    },
    {
      id: 'spend',
      accessorFn: (row) => spendValue(row),
      enableSorting: !spendLocked,
      header: ({ column }) => (
        <HeaderCell column={column} name={lockableName('Spend')} numberColumn />
      ),
      cell: ({ row }) =>
        spendLocked ? (
          lockedCell
        ) : (
          <NumberCell
            number={spendValue(row.original) as number}
            formatter={(value) => `${symbol}${formatCompact(value)}`}
          />
        ),
    },
    {
      id: 'spendPerKg',
      accessorFn: (row) => spendPerKgValue(row),
      enableSorting: !spendLocked,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name={lockableName('Average price')}
          unit={<Unit variant={`${symbol}/kg`} />}
          numberColumn
        />
      ),
      cell: ({ row }) => {
        if (spendLocked) return lockedCell;
        const value = spendPerKgValue(row.original);
        // Plain hyphen for nulls (formatCost's em dash is off-convention).
        return (
          <TextCell
            text={value != null ? formatCost(value, currency) : '-'}
            type="number"
          />
        );
      },
    },
    {
      id: 'emissionsPerSpendUnit',
      accessorKey: 'emissionsPerSpendUnit',
      enableSorting: false,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name={lockableName('Emissions per spend')}
          unit={
            // Currency-suffixed variant of the standard unit treatment.
            <Text variant="unit" className="text-xs leading-[12px]">
              {renderUnit('kgCO2e')}/{symbol}
            </Text>
          }
          numberColumn
        />
      ),
      cell: ({ row }) =>
        spendLocked ? (
          lockedCell
        ) : (
          <NumberCell number={row.original.emissionsPerSpendUnit as number} />
        ),
    },
  ];
}
